import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from './constants';
import { useAuth } from './AuthContext';
import "./ChatView.css"; // Optional: You can have specific styling for ChatView

const ChatView = () => {
  const { projectKey } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Welcome! I'm here to help you analyze product data. You can ask me any question about products, pricing, promotions, market trends, or any other insights from our data. What would you like to know?"
    }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("chatbot");
  const [exampleQuestions, setExampleQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  // Fetch example questions from backend based on project_key
  useEffect(() => {
    const fetchExampleQuestions = async () => {
      if (!projectKey) {
        setLoadingQuestions(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/chat-example-questions`, {
          params: {
            project_key: projectKey
          },
          withCredentials: true
        });
        if (response.data && response.data.questions && response.data.questions.length > 0) {
          setExampleQuestions(response.data.questions);
          console.log('Loaded example questions from backend:', response.data.questions);
        } else {
          setExampleQuestions([]);
        }
      } catch (error) {
        console.error("Error fetching example questions:", error);
        setExampleQuestions([]);
      } finally {
        setLoadingQuestions(false);
      }
    };

    fetchExampleQuestions();
  }, [projectKey]);

  const handleExampleClick = async (example) => {
    setMode("chatbot"); // Switch to chatbot mode when using examples
    
    const newMessages = [...messages, { role: "user", content: example }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/ask`, {
        question: example,
      }, {
        withCredentials: true
      });

      setMessages([...newMessages, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Error fetching response:", error);
      setMessages([...newMessages, { role: "assistant", content: "Error fetching response." }]);
    }

    setLoading(false);
  };

  // Function to format text with italic
  const formatText = (text) => {
    if (!text) return '';
    
    // Replace **text** with <em>text</em> (italic)
    let formattedText = text.replace(/\*\*(.*?)\*\*/g, '<em>$1</em>');
    
    // Replace *text* with <em>text</em> (italic)
    formattedText = formattedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    // Split into paragraphs and wrap in <p> tags
    const paragraphs = formattedText.split('\n\n').filter(p => p.trim());
    const formattedParagraphs = paragraphs.map(p => `<p>${p.trim()}</p>`).join('');
    
    return formattedParagraphs;
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    const newMessages = [...messages, { role: "user", content: input }];
    setMessages(newMessages);
    setLoading(true);

    try {
      let response;

      if (mode === "price_sensitivity") {
        const match = input.match(/([\w\s]+)\s+(\d+)%/);
        if (!match) {
          throw new Error("Invalid format. Use: 'Zyn 10%'");
        }
        const product = match[1].trim();
        const discount = parseFloat(match[2]);

        response = await axios.post(`${API_URL}/price_sensitivity`, {
          products: [product],
          proposed_discount: discount,
        }, {
          withCredentials: true
        });
      } else {
        response = await axios.post(`${API_URL}/ask`, {
          question: input,
        }, {
          withCredentials: true
        });
      }

      setMessages([...newMessages, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      console.error("Error fetching response:", error);
      setMessages([...newMessages, { role: "assistant", content: "Error fetching response." }]);
    }

    setLoading(false);
    setInput("");
  };

  return (
    <div className="chat-layout">
      <div className="chat-main">
        <div className="chat-container">
          <div className="mode-selector">
            <label>Select Mode:</label>
            <select value={mode} onChange={(e) => {
              // Temporarily disable price_sensitivity mode
              if (e.target.value !== "price_sensitivity") {
                setMode(e.target.value);
              }
            }}>
              <option value="chatbot">Chatbot Mode</option>
              <option value="price_sensitivity" disabled>Price Sensitivity Mode (Temporarily Disabled)</option>
            </select>
          </div>

                     <div className="chat-box">
             {messages.map((msg, index) => (
               <div key={index} className={`chat-message ${msg.role}`}>
                 <strong>{msg.role === "user" ? "You" : "Assistant"}:</strong>
                 <div dangerouslySetInnerHTML={{ __html: formatText(msg.content) }} />
               </div>
             ))}
             {loading && <div className="loading">Thinking...</div>}
           </div>

          <div className="input-box">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && sendMessage()}
              placeholder={mode === "chatbot" ? "Ask any question about the product data..." : "Enter product & discount (e.g., Zyn 10%)"}
            />
          </div>
        </div>
      </div>

      <div className="examples-sidebar">
        <div className="examples-container">
          <h3>Product Data Examples</h3>
          <p className="examples-subtitle">Ask any question about the product data. Click on any example to get started:</p>
          <div className="examples-list">
            {loadingQuestions ? (
              <div>Loading examples...</div>
            ) : exampleQuestions.length > 0 ? (
              exampleQuestions.map((example, index) => (
                <button
                  key={index}
                  className="example-button"
                  onClick={() => handleExampleClick(example)}
                >
                  {example}
                </button>
              ))
            ) : (
              <div>No example questions available.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatView;